<template>
    <tr class="generic-module draggable" role="row" v-bind:class="{ inactive: inactiveRow }">
        <!-- Index and drag handle -->
        <td class="controls compact">
            <span class="drag-handle"><fa-icon icon="grip-lines"></fa-icon></span>
            <span class="index">{{ index }} </span>
        </td>

        <!-- Module name and variant -->
        <td>
            <span class="type">{{ label }}</span><span class="name" v-if="hasVariant">: {{ variant.name }}</span>
        </td>

        <!-- Scheduled display dates -->
        <td>
            <date-picker
                updateOnInput
                ref="calendar"
                v-model="dates"
                mode="range"
                :id="this.getId('date_picker')"
                :popover="{ placement: 'bottom', visibility: 'click' }"
                @input="changeDateEvent"
            >
                <button class="btn btn-outline-secondary dates-btn" v-bind:class="{ active: hasDates }">
                    <span v-if="hasDates">{{ startDate }} - {{ endDate }}</span>
                    <fa-icon v-else icon="calendar-alt"></fa-icon>
                </button>
            </date-picker>

            <tippy
                interactive
                :animate-fill="false"
                placement="top"
                theme="light-border"
                animation="fade"
                trigger="click"
                arrow
                class="d-inline-block">
                <template v-slot:trigger>
                    <button class="btn btn-outline-secondary" v-bind:class="{ invisible: !hasDates }">
                        <span v-if="hasDates">{{ startTime }} - {{ endTime }}</span>
                        <fa-icon v-else icon="clock"></fa-icon>
                    </button>
                </template>
                <div>
                    <div class="form-group d-inline-block">
                        <label class="d-block font-weight-bold">Start Time</label>
                        <time-picker :minute-interval="5" placeholder="12:00" input-class="text-center"
                                     v-model="time.start" @change="changeDateEvent"/>
                    </div>
                    <div class="form-group d-inline-block">
                        <label class="d-block font-weight-bold">End Time</label>
                        <time-picker :minute-interval="5" placeholder="12:00" input-class="text-center"
                                     v-model="time.end" @change="changeDateEvent"/>
                    </div>
                </div>
            </tippy>

            <button class="btn btn-link text-muted" v-if="hasDates" v-on:click="deleteDates">
                <fa-icon icon="times-circle"></fa-icon>
            </button>
        </td>

        <!-- Shuffle Switch -->
        <td>
            <div class="form-check form-check-inline" v-if="canShuffle">
                <label class="switch">
                    <input
                        type="checkbox"
                        :name=this.getId()
                        :id="this.getId('shuffling')"
                        :checked="isChecked('true', 'shuffling')"
                        @change="changeCollectionEvent"
                        value="true"
                    />
                    <span class="slider round"></span>
                </label>
            </div>
        </td>

        <!-- Display Type -->
        <td>
            <div class="form-check form-check-inline" v-if="canSelectDisplayType">
                <select
                    class="custom-select custom-select-sm"
                    ref="displayType"
                    :id="this.getId('display_type')"
                    @change="changeCollectionEvent"
                >
                    <option v-for="(type, index) in displayTypes" :value="type.value" :key="index">
                        {{ type.label }}
                    </option>
                </select>
            </div>
        </td>

        <!-- Image options for regular modules -->
        <td class="compact" v-if="!isBanner">

            <div class="form-check form-check-inline">
                <input class="form-check-input"
                       type="radio"
                       :name=this.getId()
                       :id="this.getId('image_size')"
                       :checked="isChecked('small', 'imageSize')"
                       @change="changeCollectionEvent"
                       value="small"/>
                <label class="form-check-label" :for="this.getId('small')">Small</label>
            </div>
            <div class="form-check form-check-inline">
                <input class="form-check-input"
                       type="radio"
                       :name=this.getId()
                       :id="this.getId('image_size')"
                       :checked="isChecked('large', 'imageSize')"
                       @change="changeCollectionEvent"
                       value="large"/>
                <label class="form-check-label" :for="this.getId('large')">Large</label>
            </div>
        </td>

        <!-- Image options for banner type modules -->
        <td class="compact" v-if="isBanner">
            <fileUploadButton :url="bannerImage" :endpoint="'/banner'" @file-uploaded="changeBannerEvent"
                              @file-deleted="changeBannerEvent({url:null})"/>
        </td>

        <!-- Delete button -->
        <td class="controls compact">
            <span v-on:click="$emit('delete', index)" class="clickable btn btn-danger">
                <fa-icon icon="trash-alt"></fa-icon>
            </span>
        </td>
    </tr>
</template>

<script>
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import VueTimepicker from 'vue2-timepicker';
import 'vue2-timepicker/dist/VueTimepicker.css';
import moment from 'moment';
import Vue from "vue";

Vue.component('date-picker', DatePicker)
Vue.component('time-picker', VueTimepicker)
Vue.component('fileUploadButton', require('../FileUploadButton/FileUploadButton').default);


export default {
    props: {
        index: String,
        label: String,
        variant: Object,
        deleteBtnText: String,
        imageSize: String,
        shuffling: String,
        bannerImage: String,
        selectedDisplayType: String,
        displayTypes: Array,
        selectedDates: Object,
    },
    data: function () {
        return {
            dates: {
                start: this._props.selectedDates ? new Date(this._props.selectedDates.start) : null,
                end: this._props.selectedDates ? new Date(this._props.selectedDates.end) : null,
            },
            time: {
                start: this._props.selectedDates ? moment(this._props.selectedDates.start).format('HH:mm') : '00:00',
                end: this._props.selectedDates ? moment(this._props.selectedDates.end).format('HH:mm') : '00:00',
            },
        }
    },
    methods: {
        getId: function (type) {
            let id = `opt-${this.index}`;
            if (type) {
                id = `${id}-${type}`;
            }
            return id;
        },
        refresh() {

        },
        isChecked: function (type, paramName) {
            return type === this[paramName];
        },
        changeCollectionEvent(event) {
            this.$emit('changeCollection', event);
        },
        changeDateEvent(e) {
            if (!this.dates || !this.dates.start) return;

            this.time.start = this.time.start || '00:00';
            this.time.end = this.time.end || '00:00';
            this.$emit('changeDates', {date: this.dates, time: this.time});
        },
        changeBannerEvent(e) {
            this.$emit('changeCollection', {
                target: {
                    id: '--banner_image',
                    value: e.url
                }
            });
        },
        deleteDates() {
            this.$emit('changeDates', {date: null, time: null});
            this.$set(this.dates, 'start', null);
            this.$set(this.dates, 'end', null);
            this.$set(this.time, 'start', null);
            this.$set(this.time, 'end', null);
        },
    },
    computed: {
        hasVariant: function () {
            if (!this.variant || !this.variant.id) return false;

            return true;
        },
        hasDates: function () {
            return (this.dates && this.dates.start && this.dates.end);
        },
        hasImageSize: function () {
            return false;
        },
        isBanner: function () {
            let displayType = this._props.selectedDisplayType;
            if (displayType) {
                return displayType.search('banner') !== -1;
            }
            return false;
        },
        inactiveRow: function () {
            // No dates are selected
            if (!this.dates || (!this.dates.start && !this.dates.end)) return false;
            const today = new Date();
            // Include the end date in comparision
            const endOfEndDay =
                new Date(this.dates.end.getFullYear()
                    , this.dates.end.getMonth()
                    , this.dates.end.getDate()
                    , 23, 59, 59);
            return !(today >= this.dates.start && today <= endOfEndDay);

        },
        canShuffle: function () {
            if (this.label === 'Genre') return true;
            if (this.label === 'Collection') return true;
            return this.label === 'Authors';

        },
        canSelectDisplayType: function () {
            // Must match one of the allowed_modules
            const allowed_modules = [
                'Collections',
                'Collection',
                'Genre',
                'Latest Snicks',
                'Authors',
                'Author'
            ];
            if (allowed_modules.indexOf(this.label) === -1) return false;

            // Must have display types
            return this._props.displayTypes.length !== 0;
        },
        startDate: function () {
            return moment(this.dates.start).format('YYYY-MM-DD');
        },
        endDate: function () {
            return moment(this.dates.end).format('YYYY-MM-DD');
        },
        startTime: function () {
            return this.time.start;
        },
        endTime: function () {
            return this.time.end;
        }
    }
};
</script>
<style scoped>
.inactive .type,
.inactive .name,
.inactive .index {
    opacity: 0.4;
}

/* The switch - the box around the slider */
.switch {
    position: relative;
    display: inline-block;
    width: 52px;
    height: 25px;
}

/* Hide default HTML checkbox */
.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

/* The slider */
.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    -webkit-transition: .4s;
    transition: .4s;
}

.slider:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    -webkit-transition: .4s;
    transition: .4s;
}

input:checked + .slider {
    background-color: #2196F3;
}

input:focus + .slider {
    box-shadow: 0 0 1px #2196F3;
}

input:checked + .slider:before {
    -webkit-transform: translateX(26px);
    -ms-transform: translateX(26px);
    transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
    border-radius: 34px;
}

.slider.round:before {
    border-radius: 50%;
}

.dates-btn {
    margin-bottom: 5px;
}
</style>
